import { isObject } from '../../type_guards'
import type { ODataPreference } from './http'
import type { HttpContext } from '@adonisjs/core/http'
import type { ApplicationService } from '@adonisjs/core/types'
import type { ResourcefulModel } from '@nhtio/lucid-resourceful/types'

export const isSimpleIdentifier = (name: unknown): boolean => {
  if (typeof name !== 'string') {
    return false
  }
  // Must be 1–128 Unicode code points
  if (!name || [...name].length < 1 || [...name].length > 128) {
    return false
  }

  // Regex based on OData "SimpleIdentifier" rules
  // First character: underscore (_) or Unicode category Letter (L) or Letter Number (Nl)
  // Remaining characters: underscore, Letter, Letter Number, Decimal Number, Non-spacing mark,
  // Combining spacing mark, Connector punctuation, or Other Format
  const simpleIdentifierRegex = /^[_\p{L}\p{Nl}][_\p{L}\p{Nl}\p{Nd}\p{Mn}\p{Mc}\p{Pc}\p{Cf}]*$/u

  return simpleIdentifierRegex.test(name)
}

export const sanitizeSelect = async (
  query: Record<string, any>,
  model: ResourcefulModel,
  ctx: HttpContext,
  app: ApplicationService
): Promise<{ valid: string[] | undefined; isFullSet: boolean }> => {
  const potential: string[] = []
  if (typeof query['$select'] === 'string') {
    potential.push(
      ...query['$select']
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    )
  } else if (Array.isArray(query['$select'])) {
    potential.push(...query['$select'].map((s) => String(s).trim()).filter((s) => s.length > 0))
  }
  const readSchema = await model.$asOpenApiSchemaObject(ctx, app, 'read')
  const possible =
    !isObject(readSchema) || !('properties' in readSchema) || !isObject(readSchema.properties)
      ? []
      : Object.keys(readSchema.properties)
  if (!('$select' in query)) {
    // Default selection for OData should avoid relationship objects unless explicitly expanded/selected.
    // Returning relationship fields by default causes deep/cyclic payloads and unstable serialization.
    const relationshipNames = new Set(
      Array.from(model.$resourcefulRelationships.keys()).map((k) => k.toLowerCase())
    )
    const scalarFields = possible.filter((k) => !relationshipNames.has(String(k).toLowerCase()))
    if (!scalarFields.includes(model.primaryKey)) {
      scalarFields.unshift(model.primaryKey)
    }
    return { valid: scalarFields, isFullSet: true }
  }
  if (potential.length === 0) {
    return { valid: undefined, isFullSet: false }
  }
  if (potential.includes('*')) {
    // return possible
    return { valid: possible.filter((k) => k !== model.primaryKey), isFullSet: true }
  }
  const valid = potential.filter((p) => possible.includes(p))
  if (valid.length === 0) {
    return { valid: undefined, isFullSet: false }
  }
  if (!valid.includes(model.primaryKey)) {
    valid.unshift(model.primaryKey)
  }
  return { valid, isFullSet: valid.length === possible.length - 1 }
}

export const sanitizeExpand = (query: Record<string, any>): string[] | undefined => {
  const potential: string[] = []
  if (typeof query['$expand'] === 'string') {
    potential.push(
      ...query['$expand']
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    )
  } else if (Array.isArray(query['$expand'])) {
    potential.push(...query['$expand'].map((s) => String(s).trim()).filter((s) => s.length > 0))
  }
  if (potential.length === 0) {
    return
  }
  return potential
}

export const santizeResponsePayload = (
  payload: any,
  selections: string[] | undefined,
  preference: ODataPreference = 'representation'
): any => {
  if (!isObject(payload)) {
    return payload
  }
  if ('minimal' === preference && !Array.isArray(selections)) {
    return undefined
  }
  const ret: any = {}
  if (Array.isArray(selections)) {
    for (const key of selections) {
      if (key in payload) {
        ret[key] = payload[key]
      }
    }
  } else {
    Object.assign(ret, payload)
  }
  return ret
}

export function sanitizeODataKeys(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    !(typeof Buffer !== 'undefined' && value instanceof Buffer)
  ) {
    if ((value as any).isLuxonDateTime === true) {
      return value
    }
    if (seen.has(value)) {
      return undefined
    }
    seen.add(value)

    const result: Record<string, unknown> = {}
    for (const key in value as Record<string, unknown>) {
      const sanitizedKey = key.startsWith('@') ? key : key.replace(/\./g, '__')
      result[sanitizedKey] = sanitizeODataKeys((value as Record<string, unknown>)[key], seen)
    }
    return result
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeODataKeys(item, seen))
  }
  return value
}

export function desanitizeODataKeys(
  value: unknown,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    !(typeof Buffer !== 'undefined' && value instanceof Buffer)
  ) {
    if ((value as any).isLuxonDateTime === true) {
      return value
    }
    if (seen.has(value)) {
      return undefined
    }
    seen.add(value)

    const result: Record<string, unknown> = {}
    for (const key in value as Record<string, unknown>) {
      const desanitizedKey = key.startsWith('@') ? key : key.replace(/__/g, '.')
      result[desanitizedKey] = desanitizeODataKeys((value as Record<string, unknown>)[key], seen)
    }
    return result
  }
  if (Array.isArray(value)) {
    return value.map((item) => desanitizeODataKeys(item, seen))
  }
  return value
}
